import {trackEvent} from '~/shared/services/analytics';
import {
  AGE_RESTRICTED_ANALYTICS,
  DeliveryMethod,
} from '~/shared/consts/restaurantConsts';
import {pushRoute, replaceRoute} from '~/shared/router';
import {getMenuOrDishPagePath} from '~/shared/services/urlService';
import {signInModalOrPage} from '~/shared/services/navigation';
// TODO: move types to corresponding folder (DishWithSub -> dish types)
import {
  DishWithSub,
  selectCategoriesListOfCurrentRestaurant,
  selectCheckoutErrors,
  selectCurrentCoupon,
  selectCurrentDeliveryMethod,
  selectCurrentRestaurant,
  selectDishesAdditionalDetails,
  selectDishesWithSubs,
  selectIsDeliveringToCurrentAddress,
  selectShoppingCartBillingLines,
  selectRestaurantErrorModalName,
  selectShouldOpenModal,
  selectUserData,
} from '~/shared/store/selectors';
import store from '~/shared/store';
import {
  setCurrentModal as setCurrentModalAction,
} from '~/shared/store/actions';
import {DishFromCurrentRestaurant, getDishTotalPrice} from '~/shared/utils/billingLinesCalculation';
import {getRestaurantErrorModalArgs} from '~/shared/utils/restaurants/closedRestaurant';
import {isJustEat} from '~/shared/config';

import {Coupon, IShoppingCart, OrderData, OrderPermits, RestaurantFromGet, ShoppingCartDish, User} from '../store/models';
import ManagerProvider from '../managers/ManagerProvider';

interface CheckoutProps {
  billingLines: IShoppingCart['billingLines'];
  dishes: ShoppingCartDish[] | null;
  dishesWithSubs: DishWithSub;
  userData: User | null;
  currentCoupon: Coupon | null;
  deliveryMethod: DeliveryMethod;
  isDeliveringToCurrentAddress?: boolean;
  permits?: OrderPermits;
  restaurant?: RestaurantFromGet;
  isReorder?: boolean;
}

export interface OnCheckoutOpenProps extends CheckoutProps {
  setCurrentModal: (...args: any) => void;
  shouldOpenModal: boolean;

  shouldShowAgeConfirm?: boolean;
  isShoppingCartPage?: boolean;
}

export const onCheckoutOpen = async (props: OnCheckoutOpenProps) => {
  const {
    setCurrentModal,
    userData,
    restaurant,
    shouldOpenModal,
    dishes,
    dishesWithSubs,
    shouldShowAgeConfirm,
    isReorder,
    isShoppingCartPage,
  } = props;

  const {modalName: restaurantErrorModalName, isB2BUser, isRestaurant} = selectRestaurantErrorModalName(store.getState());
  if (restaurantErrorModalName) {
    const modalArgs = getRestaurantErrorModalArgs({
      modalName: restaurantErrorModalName,
      dispatch: store.dispatch,
      isRestaurant,
      showCodeButton: Boolean(isB2BUser),
    });
    if (modalArgs) {
      setCurrentModal(restaurantErrorModalName, modalArgs);
      return;
    }
  }

  if (!userData) {
    const menuOrDishPage =
      getMenuOrDishPagePath({
        restaurantId: restaurant?.id,
        restaurantName: restaurant?.name,
      }) || '/';

    return signInModalOrPage({
      mode: isJustEat ? 'signup' : 'login',
      returnUrl: menuOrDishPage,
      successReturnUrl: `${menuOrDishPage}?openCheckout=true`,
      fromCheckout: true,
      onSuccess: async newUserData => {
        onCheckoutOpen({...props, userData: newUserData || null});
      },
    });
  }

  const orderError = selectCheckoutErrors(store.getState());
  if (orderError) {
    openCheckoutErrorMessageModal({
      ...orderError,
      shouldShowAgeConfirm: Boolean(shouldShowAgeConfirm),
    });
    if (orderError.modalName === 'MinimumOrderErrorModal') {
      trackEvent('hasViewedMinimumOrderPopup');
    }
    return;
  }

  if (shouldShowAgeConfirm) {
    setCurrentModal('age_confirm_modal', {
      onConfirm: () => {
        onCheckoutOpen({...props, shouldShowAgeConfirm: false});
      },
    });
    return;
  }

  const categoriesList = selectCategoriesListOfCurrentRestaurant(store.getState());

  const productPrice = (dish: ShoppingCartDish) => {
    const dishForPrice: DishFromCurrentRestaurant = {
      choices: dishesWithSubs[dish.dishId].choices,
      dishPrice: dish.dishPrice ? dish.dishPrice : 0,
    };
    return getDishTotalPrice(dishForPrice, dish.choices, 1);
  };

  const currentProducts = dishes?.map(dish => ({
    productID: dish.dishId,
    productPrice: dish.choices ? productPrice(dish) : dish.dishPrice,
    productName: dish.dishName,
    productQuantity: dish.quantity,
    productCategory: categoriesList.find(item => item.categoryId === dish.categoryId)?.categoryName,
    productVariant: dishesWithSubs?.[dish.dishId].ageRestricted ? AGE_RESTRICTED_ANALYTICS : '',
  }));

  trackEvent('hasBeginCheckout', currentProducts);

  if (isReorder) {
    dishes?.map(async dish => {
      await ManagerProvider.addAllPaymentsForReorder(dish);
    });
  }

  if (shouldOpenModal) {
    setCurrentModal('checkout_modal');
    return;
  }

  if (isShoppingCartPage) {
    // replacing route to allow user to go back to menu page when clicking back button from checkout page
    // instead of going back to shopping cart page
    replaceRoute('/checkout');
    return;
  }

  pushRoute('/checkout');
};

export const removeItemsFromCart = async (unavailableDishesIds: UnavailableDishId[]) => {
  unavailableDishesIds.forEach(async unavailableDishId => {
    await ManagerProvider.removeDish(unavailableDishId);
  });
};

type OpenCheckoutErrorMessageModalProps = ReturnType<typeof selectCheckoutErrors> & {
  shouldShowAgeConfirm?: boolean;
  modalOptions?: Record<string, unknown>;
};

export function openCheckoutErrorMessageModal(props: OpenCheckoutErrorMessageModalProps) {
  const state = store.getState();

  const onSuccess = (permits: OrderData['permits']) => {
    onCheckoutOpen({
      permits,
      setCurrentModal: (name, args: Record<string, unknown>) => store.dispatch(setCurrentModalAction(name, args)),
      billingLines: selectShoppingCartBillingLines(state),
      userData: selectUserData(state),
      restaurant: selectCurrentRestaurant(state),
      deliveryMethod: selectCurrentDeliveryMethod(state),
      currentCoupon: selectCurrentCoupon(state),
      dishes: selectDishesAdditionalDetails(state),
      isDeliveringToCurrentAddress: selectIsDeliveringToCurrentAddress(state),
      dishesWithSubs: selectDishesWithSubs(state),
      shouldShowAgeConfirm: props.shouldShowAgeConfirm,
      shouldOpenModal: selectShouldOpenModal(state),
    });
  };

  store.dispatch(setCurrentModalAction('checkout_error_message_with_code_modal', {
    ...props,
    onSuccess,
    dispatch: store.dispatch,
  }));
}

export type UnavailableDishId = ShoppingCartDish['shoppingCartDishId'];
